<template>
  <div class="ftp-mode">

    <!-- Le switch : par défaut activé, donc rien ne change pour l'existant -->
    <div class="mode-switch">
      <div class="mode-switch-text">
        <div class="mode-switch-title">
          <span class="material-symbols-outlined">{{ isCustom ? 'tune' : 'dns' }}</span>
          <h4>{{ isCustom ? 'Configuration FTP propre à cet univers' : 'Sources FTP partagées' }}</h4>
        </div>
        <p class="mode-switch-hint" v-if="isCustom">
          Cet univers se connecte lui-même au FTP, avec ses propres identifiants.
          C'est le fonctionnement historique, inchangé.
        </p>
        <p class="mode-switch-hint" v-else>
          Cet univers lit une ou plusieurs sources déclarées ailleurs. Le
          téléchargement est mutualisé, et la donnée survit à la suppression de
          l'univers.
        </p>
      </div>

      <v-switch
          :input-value="isCustom"
          :true-value="1"
          :false-value="0"
          inset
          hide-details
          :disabled="!editable"
          color="#2d5a3d"
          label="Custom"
          @change="onToggle"
      ></v-switch>
    </div>

    <!-- Mode custom : le formulaire FTP historique, intact -->
    <div v-if="isCustom">
      <FtpForm ref="FtpForm" v-model="value" :editable="editable"/>

      <div class="promote-box" v-if="editable && hasInlineConfig">
        <span class="material-symbols-outlined">upgrade</span>
        <div class="promote-text">
          <strong>Promouvoir en source partagée</strong>
          <p>
            Crée un serveur FTP à partir de cette configuration et y rattache cet
            univers. Si une source identique existe déjà, l'univers y est rattaché
            au lieu d'en créer une seconde.
          </p>
        </div>
        <button type="button" class="promote-btn" :disabled="promoting" @click="promote">
          {{ promoting ? 'En cours…' : 'Promouvoir' }}
        </button>
      </div>
    </div>

    <!-- Mode serveur : sélection des sources -->
    <div v-else class="servers-mode">
      <div v-if="loading" class="mode-empty">Chargement des sources…</div>

      <div v-else-if="!servers.length" class="mode-empty">
        Aucune source déclarée pour l'instant. Créez-en une depuis
        <strong>Serveurs FTP</strong> dans le menu d'administration.
      </div>

      <div v-else>
        <p class="mode-hint">
          Rattacher une source donne accès à <strong>tout son historique</strong>
          immédiatement. Détacher le masque. La donnée n'est jamais déplacée ni
          supprimée.
        </p>

        <div class="server-list">
          <label
              v-for="server in servers"
              :key="server.id"
              class="server-item"
              :class="{ selected: selectedIds.includes(server.id), disabled: !editable }"
          >
            <input
                type="checkbox"
                :disabled="!editable"
                :checked="selectedIds.includes(server.id)"
                @change="toggle(server)"
            />

            <span class="server-body">
              <span class="server-name">
                {{ server.name }}
                <span class="server-chip" v-if="!server.active">inactif</span>
                <span class="server-chip warn" v-if="server.start_date">
                  depuis {{ server.start_date.substring(0, 10) }}
                </span>
              </span>
              <span class="server-location">{{ server.location }}</span>
            </span>

            <span class="server-meta">
              <span class="server-doc">Site {{ server.doc_site }}</span>
              <span class="server-count">{{ server.site_ids.length }} univers</span>
            </span>
          </label>
        </div>

        <div class="mode-actions" v-if="editable">
          <span class="mode-dirty" v-if="dirty">Modifications non enregistrées</span>
          <button type="button" class="mode-save" :disabled="!dirty || saving" @click="save">
            <span class="material-symbols-outlined">save</span>
            {{ saving ? 'Enregistrement…' : 'Enregistrer les sources' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FtpForm from "./FtpForm";

export default {
  name: "SiteFtpMode",

  components: {FtpForm},

  props: {
    value: {type: Object, required: true},
    editable: {type: Boolean, default: true},
  },

  data() {
    return {
      loading: false,
      saving: false,
      promoting: false,

      servers: [],
      selectedIds: [],
      initialIds: "",
    };
  },

  computed: {
    // Défaut à 1 : un univers sans réglage reste en mode historique.
    isCustom() {
      const v = this.value.ftp_custom;
      return v === undefined || v === null ? 1 : Number(v);
    },

    hasInlineConfig() {
      return !!(this.value.ftp_host && this.value.ftp_user && this.value.ftp_path);
    },

    dirty() {
      return JSON.stringify([...this.selectedIds].sort()) !== this.initialIds;
    },
  },

  created() {
    if (!this.isCustom) {
      this.load();
    }
  },

  methods: {
    onToggle(v) {
      const next = Number(v) ? 1 : 0;

      this.$set(this.value, "ftp_custom", next);

      if (!next && !this.servers.length) {
        this.load();
      }
    },

    async load() {
      this.loading = true;

      try {
        const data = await this.$store.dispatch("ftpServersRequest");
        this.servers = data.servers || [];

        this.selectedIds = this.servers
            .filter((s) => s.site_ids.includes(this.value.id))
            .map((s) => s.id);

        this.initialIds = JSON.stringify([...this.selectedIds].sort());
      } catch (err) {
        this.showErr(err);
      } finally {
        this.loading = false;
      }
    },

    toggle(server) {
      const i = this.selectedIds.indexOf(server.id);

      if (i >= 0) {
        this.selectedIds.splice(i, 1);
      } else {
        this.selectedIds.push(server.id);
      }
    },

    /**
     * L'abonnement est porté par la source : on met à jour chaque serveur dont
     * la liste d'univers change, et seulement ceux-là.
     */
    async save() {
      this.saving = true;

      try {
        const siteId = this.value.id;

        for (const server of this.servers) {
          const was = server.site_ids.includes(siteId);
          const now = this.selectedIds.includes(server.id);

          if (was === now) {
            continue;
          }

          const ids = now
              ? [...server.site_ids, siteId]
              : server.site_ids.filter((id) => id !== siteId);

          await this.$store.dispatch("ftpServerSaveRequest", {
            id: server.id,
            site_ids: ids,
          });
        }

        this.$store.commit("setSnack", {type: "green", err: "Sources mises à jour."});

        await this.load();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.saving = false;
      }
    },

    async promote() {
      this.promoting = true;

      try {
        const result = await this.$store.dispatch("ftpServerPromoteRequest", {
          siteId: this.value.id,
          switch: true,
        });

        this.$set(this.value, "ftp_custom", 0);

        this.$store.commit("setSnack", {
          type: "green",
          err: result.created
              ? `Source « ${result.server.name} » créée et rattachée.`
              : `Univers rattaché à la source existante « ${result.server.name} ».`,
        });

        await this.load();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.promoting = false;
      }
    },

    showErr(err) {
      const data = err && err.response && err.response.data;
      const message = (data && data.message) || data || "Une erreur est survenue";

      this.$store.commit("setSnack", {
        type: "red",
        err: typeof message === "string" ? message : JSON.stringify(message),
      });
    },
  },
};
</script>

<style scoped>
.ftp-mode {
  width: 100%;
}

.mode-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 16px;
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  background: #f9fbfa;
  margin-bottom: 18px;
}

.mode-switch-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mode-switch-title h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2d24;
}

.mode-switch-hint {
  font-size: 0.78rem;
  color: #8a9a90;
  margin: 6px 0 0;
  max-width: 620px;
}

.mode-hint,
.mode-empty {
  font-size: 0.82rem;
  color: #8a9a90;
  margin: 0 0 14px;
}

.mode-empty {
  font-style: italic;
  padding: 14px 0;
}

.server-list {
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  overflow: hidden;
}

.server-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f3f1;
  cursor: pointer;
  transition: background 0.15s;
  margin: 0;
}

.server-item:last-child {
  border-bottom: none;
}

.server-item:hover {
  background: #f6f8f7;
}

.server-item.selected {
  background: #eef5f0;
}

.server-item.disabled {
  cursor: default;
  opacity: 0.7;
}

.server-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.server-name {
  font-size: 0.88rem;
  font-weight: 500;
  color: #1f2d24;
  display: flex;
  align-items: center;
  gap: 6px;
}

.server-chip {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #ececec;
  color: #8a8a8a;
  padding: 1px 6px;
  border-radius: 4px;
}

.server-chip.warn {
  background: #eef5f0;
  color: #2d5a3d;
}

.server-location {
  font-family: monospace;
  font-size: 0.72rem;
  color: #8a9a90;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.server-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 0.68rem;
  color: #a0aca5;
  flex-shrink: 0;
}

.server-doc {
  font-family: monospace;
}

.mode-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}

.mode-dirty {
  font-size: 0.76rem;
  color: #c9822a;
}

.mode-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #2d5a3d;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
}

.mode-save:disabled {
  opacity: 0.45;
  cursor: default;
}

.mode-save .material-symbols-outlined {
  font-size: 18px;
}

.promote-box {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px dashed #c8d6cd;
  border-radius: 10px;
  background: #f9fbfa;
}

.promote-text {
  flex: 1;
}

.promote-text strong {
  font-size: 0.88rem;
  color: #1f2d24;
}

.promote-text p {
  font-size: 0.78rem;
  color: #8a9a90;
  margin: 4px 0 0;
}

.promote-btn {
  background: #fff;
  border: 1px solid #2d5a3d;
  color: #2d5a3d;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
}

.promote-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
